import { useEffect, useRef, useState } from "react";

import { useCouponCollectionDetailQuery } from "../queries";

const MIN_VALIDATION_LOADING_MS = 3000;

interface UseValidateCouponCollectionProps {
  id?: string | null;
}

export const useValidateCouponCollection = ({
  id
}: UseValidateCouponCollectionProps) => {
  const { data, loading, error } = useCouponCollectionDetailQuery({
    skip: !id,
    variables: {
      id
    }
  });

  const loadStartedAtRef = useRef<number | null>(null);
  const [extendLoading, setExtendLoading] = useState(false);

  useEffect(() => {
    if (!id) {
      loadStartedAtRef.current = null;
      setExtendLoading(false);
      return;
    }

    if (loading) {
      loadStartedAtRef.current = Date.now();
      setExtendLoading(false);
      return;
    }

    const startedAt = loadStartedAtRef.current;
    loadStartedAtRef.current = null;

    if (startedAt === null) {
      return;
    }

    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, MIN_VALIDATION_LOADING_MS - elapsed);

    if (remaining === 0) {
      setExtendLoading(false);
      return;
    }

    setExtendLoading(true);
    const timeoutId = window.setTimeout(() => {
      setExtendLoading(false);
    }, remaining);

    return () => window.clearTimeout(timeoutId);
  }, [id, loading]);

  const collectionType = data?.couponCollection?.collectionType || null;

  return {
    collectionType,
    errorCollection: error?.message || null,
    validationLoading: loading || extendLoading
  };
};
